import { signal, Signal } from '@angular/core';

/**
 * Smoothly animates an integer counter toward a (possibly moving) target using critically damped
 * smoothing (SmoothDamp). Velocity is carried between frames, so the value eases in and out
 * without overshoot and absorbs target changes — e.g. new scan batches — without a jump in speed.
 *
 * Drive it from inside the Angular zone (the default for DOM/RxJS callbacks) so that updating the
 * `displayed` signal triggers change detection. Call `destroy()` from the owner's teardown.
 */
export class AnimatedCounter {
  private readonly value = signal(0);

  /** Current rounded value; bind this in templates. */
  public readonly displayed: Signal<number> = this.value.asReadonly();

  private exact = 0;
  private velocity = 0;
  private target = 0;
  private frameId: number | null = null;
  private lastTimestamp: number | null = null;

  /** Approximate time to reach the target; larger = slower, more gradual. */
  constructor(private readonly smoothTimeMs = 400) {}

  /** Animates from the current value toward `target` (no-op if already there). */
  public animateTo(target: number): void {
    this.target = target;

    if (this.frameId !== null || this.exact === target) {
      return;
    }

    this.lastTimestamp = null;
    this.frameId = requestAnimationFrame(this.step);
  }

  /** Jumps straight to `value` with no animation and stops any running tween. */
  public snap(value: number): void {
    this.cancel();
    this.target = value;
    this.exact = value;
    this.velocity = 0;
    this.value.set(value);
  }

  /** Stops the animation; call from the owner's teardown. */
  public destroy(): void {
    this.cancel();
  }

  private readonly step = (timestamp: number): void => {
    if (this.lastTimestamp === null) {
      this.lastTimestamp = timestamp;
    }

    // Clamp dt so a backgrounded tab does not produce one huge jump on resume.
    const deltaMs = Math.min(timestamp - this.lastTimestamp, 100);
    this.lastTimestamp = timestamp;

    const next = this.smoothDamp(this.exact, this.target, deltaMs);

    // Settle once we are within rounding distance of the target.
    if (Math.abs(this.target - next) < 0.5) {
      this.exact = this.target;
      this.velocity = 0;
      this.value.set(this.target);
      this.frameId = null;
      this.lastTimestamp = null;
      return;
    }

    this.exact = next;
    this.value.set(Math.round(next));
    this.frameId = requestAnimationFrame(this.step);
  };

  private smoothDamp(current: number, target: number, deltaMs: number): number {
    const smoothTime = Math.max(1, this.smoothTimeMs);
    const omega = 2 / smoothTime;
    const x = omega * deltaMs;
    const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    const change = current - target;
    const temp = (this.velocity + omega * change) * deltaMs;
    this.velocity = (this.velocity - omega * temp) * exp;
    let output = target + (change + temp) * exp;

    // Clamp to the target the moment `current` crosses it, to prevent overshoot.
    if (target > current === output > target) {
      output = target;
      this.velocity = 0;
    }

    return output;
  }

  private cancel(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }

    this.lastTimestamp = null;
  }
}
